import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import ProtectedRoute from './utils/ProtectedRoute';
import AdminRoute from './utils/AdminRoute';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import { getUserInfo } from './api/auth';
import './App.css';

// 登录路由守卫组件（已登录用户访问登录页时跳转到首页）
const LoginRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (isAuthenticated) {
    // 如果是管理员，跳转到后台，否则跳转到首页
    return <Navigate to={user?.role === 'admin' ? '/admin' : '/'} replace />;
  }
  return children;
};

function App() {
  const { initAuth } = useAuthStore();
  const { currentTheme, getCurrentThemeConfig } = useThemeStore();

  // 应用启动时初始化认证状态，并拉取最新用户信息（含头像）以保持刷新后头像正确显示
  useEffect(() => {
    initAuth();
    const { token, updateUser } = useAuthStore.getState();
    if (token) {
      getUserInfo()
        .then((res) => {
          if (res.code === 200 && res.data) {
            updateUser(res.data);
            // 同步到 localStorage，便于 initAuth 下次读取完整信息（含头像）
            localStorage.setItem('user_info', JSON.stringify(res.data));
          }
        })
        .catch(() => {
          // 静默失败，继续使用本地缓存的用户信息
        });
    }
  }, [initAuth]);

  // Ant Design 主题配置 - 动态使用主题store中的颜色
  const themeConfig = {
    token: {
      colorPrimary: getCurrentThemeConfig().primary,
      borderRadius: 6,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
  };

  return (
    <ConfigProvider theme={themeConfig} locale={zhCN}>
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={
              <LoginRoute>
                <Login />
              </LoginRoute>
            } 
          />
          <Route 
            path="/" 
            element={<Home />}
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
